import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Chip,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  DataGrid,
  GridToolbar,
  GridActionsCellItem,
} from "@mui/x-data-grid";

import dayjs from "dayjs";
import { toast } from "react-toastify";

import AddDemoLinkDialog from "./AddDemoLinkDialog";

export default function DemoLinks() {
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const token = localStorage.getItem("token");

  const getDemoSessions = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_LIVE_URL}/demo-session`
      );

      if (res.data.status) {
        setRows(res.data.data);
      }
    } catch (err) {
      console.log(err);
      toast.error(err?.response?.data?.msg || "Unable to fetch Demo Links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await getDemoSessions();
    })();
  }, []);

  const filteredRows = rows.filter((item) => {
    return (
      item.subject
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      item.teacherName
        ?.toLowerCase()
        .includes(search.toLowerCase())
    );
  });
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this Demo Link?")) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_LIVE_URL}/demo-session/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Demo Link Deleted");

      await getDemoSessions();
    } catch (err) {
      toast.error(
        err.response?.data?.msg || "Delete Failed"
      );
    }
  };

  const handleEdit = (row) => {
    setSelectedRow({
      id: row.id,
      subject: row.subject,
      teacher: row.teacherName,
      date: row.date,
      startTime: row.startTime,
      endTime: row.endTime,
      meetLink: row.googleMeetLink,
      status: row.isActive ? "Active" : "Inactive",
    });

    setEditMode(true);
    setOpen(true);
  };

  const handleSubmitDemo = async (demo) => {
    try {
      const payload = {
        teacherName: demo.teacher,
        subject: demo.subject,
        date: demo.date,
        startTime: demo.startTime,
        endTime: demo.endTime,
        googleMeetLink: demo.meetLink,
        isActive: demo.status === "Active",
      };

      if (editMode) {
        await axios.put(
          `${process.env.REACT_APP_LIVE_URL}/demo-session/${selectedRow.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        toast.success("Demo Link Updated Successfully");
      } else {
        await axios.post(
          `${process.env.REACT_APP_LIVE_URL}/demo-session`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        toast.success("Demo Link Added Successfully");
      }

      await getDemoSessions();

      setOpen(false);
      setEditMode(false);
      setSelectedRow(null);
    } catch (err) {
      console.log(err);
      toast.error(err?.response?.data?.msg || "Something went wrong");
    }
  };

  const columns = [
    {
      field: "serial",
      headerName: "S No",
      width: 80,
      renderCell: (params) =>
        params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },
    {
      field: "subject",
      headerName: "Subject",
      flex: 1,
    },
    {
      field: "teacherName",
      headerName: "Teacher",
      flex: 1,
    },
    {
      field: "date",
      headerName: "Date",
      width: 120,
      renderCell: (params) =>
        dayjs(params.value).format("DD MMM YYYY"),
    },
    {
      field: "startTime",
      headerName: "Start Time",
      width: 120,
    },
    {
      field: "endTime",
      headerName: "End Time",
      width: 120,
    },
    {
      field: "googleMeetLink",
      headerName: "Google Meet",
      flex: 1.5,
      renderCell: (params) => (
        <a
          href={params.value}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            color: "#1976d2",
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          Join Meeting
        </a>
      ),
    },
    {
      field: "isActive",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          color={params.value ? "success" : "error"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDelete(params.id)}
        />,
      ],
    },
  ];

  return (
    <Box>
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Demo Links
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedRow(null);
            setEditMode(false);
            setOpen(true);
          }}
          sx={{
            background: "linear-gradient(90deg,#8E2DE2,#C94BFF)",
            textTransform: "none",
            borderRadius: 2,
          }}
        >
          Add Demo Link
        </Button>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by Subject or Teacher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            mb: 2,
            maxWidth: 350,
          }}
        />

        <DataGrid
          getRowId={(row) => row.id || row._id}
          loading={loading}
          autoHeight
          rows={filteredRows}
          columns={columns}
          disableRowSelectionOnClick
          getRowHeight={() => "auto"}
          pageSizeOptions={[5, 10, 20]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
              },
            },
          }}
          slots={{
            toolbar: GridToolbar,
          }}
        />
      </Paper>

      <AddDemoLinkDialog
        open={open}
        editMode={editMode}
        initialData={selectedRow}
        handleClose={() => {
          setOpen(false);
          setEditMode(false);
          setSelectedRow(null);
        }}
        onSubmit={handleSubmitDemo}
      />
    </Box>
  );
}